/**
 * ProtectedShell — #186 auth.
 *
 * Guards all authenticated routes. Reads session from useAuthStore (token presence).
 * Role-based redirect reads from auth store user.role.
 *
 * Also listens for the global "auth:unauthorized" event dispatched by client.ts
 * on 401 responses, and navigates to /login when received.
 */
import { useEffect } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/lib/stores/auth";
import type { AuthRole } from "@/lib/stores/auth";
import { useRoleStore, type AppRole } from "@/lib/stores/role";
import { Sidebar } from "@/components/shell/Sidebar";
import { Topbar } from "@/components/shell/Topbar";

interface ProtectedShellProps {
  role: AppRole;
}

const ROLE_LANDING: Record<AuthRole, string> = {
  owner: "/owner/dashboard",
  manager: "/manager/dashboard",
  trainer: "/trainer/my-referrals",
};

export function ProtectedShell({ role }: ProtectedShellProps) {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const authUser = useAuthStore((s) => s.user);
  // Legacy fallback: if auth store has no user but role store does, keep working.
  // This supports sessions established before #186 (role store persist).
  // TODO(wire-be): remove legacy fallback once all users re-authenticate.
  const legacyRole = useRoleStore((s) => s.currentRole);

  const currentRole = authUser?.role ?? legacyRole;
  const isAuthenticated = Boolean(token) || Boolean(legacyRole);

  // Listen for 401 events dispatched by apiClient.ts
  useEffect(() => {
    const handleUnauthorized = () => {
      navigate("/login", { replace: true });
    };
    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", handleUnauthorized);
  }, [navigate]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (currentRole !== role) {
    if (currentRole === null) {
      return <Navigate to="/login" replace />;
    }
    return <Navigate to={ROLE_LANDING[currentRole]} replace />;
  }

  return (
    <div className="bg-bg flex h-screen overflow-hidden">
      <Sidebar role={role} />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Topbar />
        <main
          id="main-content"
          tabIndex={-1}
          className="scrollbar-thin relative flex-1 overflow-y-auto"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
